import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Key, Eye, EyeSlash, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ChangePasswordDialog — self-service password change for any authenticated user.
 * Posts to /api/my/change-password with { current_password, new_password }.
 * Admin can also change anyone else's password via PUT /api/users/{id} (already exists).
 */
export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState({ cur: false, nxt: false, cfm: false });
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); setShow({cur:false,nxt:false,cfm:false}); };
  const close = () => { reset(); onClose?.(); };

  // Password rules — kept minimal so users don't get frustrated; backend enforces ≥6 chars.
  const rules = [
    { ok: next.length >= 6, label: 'At least 6 characters' },
    { ok: next && next !== current, label: 'Different from current password' },
    { ok: next && next === confirm, label: 'Confirmation matches' },
  ];
  const canSubmit = current && next && confirm && rules.every(r => r.ok);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/my/change-password`, {
        current_password: current, new_password: next,
      }, { withCredentials: true });
      toast.success('Password updated successfully');
      close();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not change password');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md" data-testid="change-pwd-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #047857 100%)' }}>
              <Key size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <div className="text-base font-bold">Change Password</div>
              <div className="text-[11px] font-normal text-gray-500">Update your account password</div>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 mt-2">
          {[
            { key: 'cur', label: 'Current Password', value: current, setter: setCurrent, testId: 'pwd-current' },
            { key: 'nxt', label: 'New Password',     value: next,    setter: setNext,    testId: 'pwd-new' },
            { key: 'cfm', label: 'Confirm New',      value: confirm, setter: setConfirm, testId: 'pwd-confirm' },
          ].map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-[11px] uppercase tracking-wider font-bold text-gray-500">{f.label}</Label>
              <div className="relative">
                <Input
                  type={show[f.key] ? 'text' : 'password'}
                  value={f.value}
                  onChange={(e) => f.setter(e.target.value)}
                  data-testid={f.testId}
                  className="pr-10"
                  autoComplete={f.key === 'cur' ? 'current-password' : 'new-password'}
                />
                <button
                  type="button"
                  onClick={() => setShow({ ...show, [f.key]: !show[f.key] })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label={show[f.key] ? 'Hide password' : 'Show password'}
                >
                  {show[f.key] ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
          ))}

          <div className="border rounded-lg p-3 bg-slate-50">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-2">Requirements</div>
            <ul className="space-y-1">
              {rules.map((r, i) => (
                <li key={i} className="flex items-center gap-2 text-xs">
                  {r.ok
                    ? <CheckCircle size={14} weight="fill" className="text-emerald-500" />
                    : <WarningCircle size={14} className="text-slate-300" />}
                  <span className={r.ok ? 'text-emerald-700' : 'text-slate-500'}>{r.label}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={close} disabled={busy}>Cancel</Button>
            <Button type="submit" disabled={!canSubmit || busy} className="text-white"
              style={{ background: 'linear-gradient(135deg, #1D9E75 0%, #047857 100%)' }}
              data-testid="pwd-submit">
              {busy ? 'Updating…' : 'Change Password'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
